import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './MainNav.module.css';

interface MainNavProps {
  onOpenOnboarding?: () => void;
}

/**
 * MainNav component
 * 
 * A spy-themed navigation bar for the Chess Codebreaker application.
 * Provides navigation between headquarters, training modules, and daily operations.
 */
const MainNav: React.FC<MainNavProps> = ({ onOpenOnboarding }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const scanLineRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<Array<HTMLLIElement | null>>([]);

  // Function to check if a path is active
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Function to handle mouse enter on nav items
  const handleNavItemEnter = (index: number) => {
    setHoveredItem(index);
  };

  // Function to handle mouse leave on nav items
  const handleNavItemLeave = () => {
    setHoveredItem(null);
  };

  // Update scanLine position based on hovered item
  useEffect(() => {
    const scanLine = scanLineRef.current;
    if (!scanLine) return;

    if (hoveredItem !== null && navItemRefs.current[hoveredItem]) {
      const navItem = navItemRefs.current[hoveredItem];
      const iconWrapper = navItem?.querySelector(`.${styles.iconWrapper}`);
      
      if (iconWrapper) {
        // Get the icon center position
        const { left: iconLeft, width: iconWidth } = iconWrapper.getBoundingClientRect();
        const iconCenter = iconLeft + (iconWidth / 2);
        const scanLineWidth = 100; // Width when focused
        const newLeft = iconCenter - (scanLineWidth / 2) + window.scrollX;
        
        // Apply hover styles directly without animation modifications
        scanLine.style.cssText = `
          transform: translateX(0%) !important;
          left: ${newLeft}px;
          width: ${scanLineWidth}px;
          opacity: 0.9;
          filter: brightness(1.2) contrast(1.1);
          animation-play-state: paused;
        `;
      }
    } else {
      // Restore default styles and animation
      scanLine.removeAttribute('style');
    }
  }, [hoveredItem]);

  return (
    <nav className={styles.nav}>
      {/* Horizontal scanning line - simulates intelligence scanning technology */}
      <div ref={scanLineRef} className={styles.scanLine}></div>
      <div className={styles.container}>
        <div className={styles.navBrand}>
          <Link to="/dashboard" className={styles.logoLink}>
            <span className={styles.logo}>♟️</span>
            <span className={styles.logoText}>
              <span className={styles.codeText}>CHESS</span>
              <span className={styles.breakerText}>CODEBREAKER</span>
            </span>
          </Link>
        </div>

        <button 
          className={`${styles.menuToggle} ${menuOpen ? styles.menuOpen : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className={styles.menuIcon}></span>
        </button>

        <div className={`${styles.navMenu} ${menuOpen ? styles.menuOpen : ''}`}>
          <div className={styles.circuitLines}></div>
          <ul className={styles.navLinks}>
            <li 
              ref={el => navItemRefs.current[0] = el} 
              className={styles.navItem}
              onMouseEnter={() => handleNavItemEnter(0)}
              onMouseLeave={handleNavItemLeave}
            >
              <Link 
                to="/dashboard" 
                className={`${styles.navLink} ${isActive('/dashboard') ? styles.active : ''}`}
              >
                <div className={styles.iconWrapper}>
                  <span className={styles.navIcon}>🏛️</span>
                </div>
                <span className={styles.navText}>HEADQUARTERS</span>
              </Link>
            </li>
            <li 
              ref={el => navItemRefs.current[1] = el} 
              className={styles.navItem}
              onMouseEnter={() => handleNavItemEnter(1)}
              onMouseLeave={handleNavItemLeave}
            >
              <Link 
                to="/analytics" 
                className={`${styles.navLink} ${isActive('/analytics') ? styles.active : ''}`}
              >
                <div className={styles.iconWrapper}>
                  <span className={styles.navIcon}>📊</span>
                </div>
                <span className={styles.navText}>ANALYTICS</span>
              </Link>
            </li>
            <li 
              ref={el => navItemRefs.current[2] = el} 
              className={styles.navItem}
              onMouseEnter={() => handleNavItemEnter(2)}
              onMouseLeave={handleNavItemLeave}
            >
              <Link 
                to="/training" 
                className={`${styles.navLink} ${isActive('/training') && !isActive('/training/daily') ? styles.active : ''}`}
              >
                <div className={styles.iconWrapper}>
                  <span className={styles.navIcon}>🎖️</span>
                </div>
                <span className={styles.navText}>OPERATIONS</span>
              </Link>
            </li>
            <li 
              ref={el => navItemRefs.current[3] = el} 
              className={styles.navItem}
              onMouseEnter={() => handleNavItemEnter(3)}
              onMouseLeave={handleNavItemLeave}
            >
              <Link 
                to="/training/daily" 
                className={`${styles.navLink} ${isActive('/training/daily') ? styles.active : ''}`}
              >
                <div className={styles.iconWrapper}>
                  <span className={styles.navIcon}>📡</span>
                </div>
                <span className={styles.navText}>DAILY INTEL</span>
              </Link>
            </li>
          </ul>
        </div>

        <div className={styles.navRight}>
          {onOpenOnboarding && (
            <button 
              className={styles.helpButton}
              onClick={onOpenOnboarding}
              aria-label="Open tutorial"
              title="Tutorial"
            >
              <span className={styles.helpIcon}>❓</span>
            </button>
          )}
          <div className={styles.agentStatus}>
            <span className={styles.statusDot}></span>
            <span className={styles.statusText}>AGENT ACTIVE</span>
          </div>
        </div>
      </div>
      <div className={styles.circuitBorder}></div>
    </nav>
  );
};

export default MainNav; 